import { useState, useCallback } from 'react';
import { Tool } from '../types';
import { getTools, addTool, updateTool, deleteTool } from '../services/storageService';
import { requireCompanyId } from './requireCompanyId';

export function useTools(companyId: string | null) {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = useCallback(async <T>(action: (cid: string) => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      const cid = requireCompanyId(companyId);
      return await action(cid);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unexpected error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const load = useCallback(async () => {
    const data = await runAction((cid) => getTools(cid));
    setTools(data);
    return data;
  }, [runAction]);

  const add = useCallback(async (tool: Tool) => {
    const updated = await runAction((cid) => addTool(tool, cid));
    setTools(updated);
    return updated;
  }, [runAction]);

  const update = useCallback(async (tool: Tool) => {
    const updated = await runAction((cid) => updateTool(tool, cid));
    setTools(updated);
    return updated;
  }, [runAction]);

  const remove = useCallback(async (id: string) => {
    const updated = await runAction((cid) => deleteTool(id, cid));
    setTools(updated);
    return updated;
  }, [runAction]);

  return { tools, setTools, loading, error, load, add, update, remove };
}
